'use client';

import { useState, useEffect } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Image from 'next/image';
import { projects } from '@/data/projects';
import { Project } from '@/data/projects';
import TechStack from '@/components/ui/TechStack';
import ImageGallery from '@/components/ui/ImageGallery';

type TabType = 'info' | 'screenshots' | 'preview';

export default function WebsiteDetailPage() {
  const params = useParams();
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<TabType>('info');
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  // Debug logging
  useEffect(() => {
    if (galleryOpen) {
      console.log('Gallery opened with index:', galleryIndex);
    }
  }, [galleryOpen, galleryIndex]);

  useEffect(() => {
    const projectId = params.id as string;
    const foundProject = projects.find(p => p.id === projectId);
    
    if (foundProject && foundProject.type === 'web') {
      setProject(foundProject);
      // Update page title
      document.title = `${foundProject.title} | Website | Portfolio`;
    } else {
      // Redirect to 404 or projects page if not found
      router.push('/#projects');
    }
    setLoading(false);
  }, [params.id, router]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Website Not Found</h1>
          <button
            onClick={() => router.push('/#projects')}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  const hasScreenshots = project.screenshots && project.screenshots.length > 0;
  const hasPreview = !!project.websitePreviewImage;

  const tabs = [
    { id: 'info' as TabType, label: 'Info', icon: '🌐' },
    { id: 'screenshots' as TabType, label: 'Screenshots', icon: '📸', disabled: !hasScreenshots },
    { id: 'preview' as TabType, label: 'Preview', icon: '👁️', disabled: !hasPreview },
  ].filter(tab => !tab.disabled);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'info':
        return (
          <div className="space-y-8">
            {/* Website Header */}
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white mb-4">{project.title}</h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                {project.description}
              </p>
            </div>

            {/* Website Image */}
            {project.websitePreviewImage && (
              <div className="flex justify-center">
                <div className="relative w-full max-w-4xl h-96 bg-zinc-800 rounded-2xl overflow-hidden shadow-2xl">
                  <Image
                    src={project.websitePreviewImage}
                    alt={project.title}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
                    className="object-cover object-top"
                  />
                </div>
              </div>
            )}

            {/* Technologies */}
            <div className="text-center">
              <h3 className="text-2xl font-semibold text-white mb-6">Technologies Used</h3>
              <TechStack technologies={project.technologies} maxVisible={20} />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {project.deployedUrl && (
                <a
                  href={project.deployedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white text-lg rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  <span>Visit Website</span>
                </a>
              )}
              {project.githubUrl && !project.isPrivate && (
                <a
                  href={project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-2 px-8 py-4 bg-zinc-700 hover:bg-zinc-600 text-white text-lg rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                  <span>View Code</span>
                </a>
              )}
            </div>
          </div>
        );

      case 'screenshots':
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-semibold text-white text-center mb-8">Website Screenshots</h3>
            {hasScreenshots ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {project.screenshots!.map((screenshot, index) => (
                  <div 
                    key={index} 
                    className="relative rounded-lg overflow-hidden cursor-pointer hover:scale-105 transition-transform duration-200"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setGalleryIndex(index);
                      setGalleryOpen(true);
                    }}
                  >
                    <Image
                      src={screenshot}
                      alt={`${project.title} Screenshot ${index + 1}`}
                      width={600}
                      height={400}
                      className="w-full h-auto object-contain"
                      priority={index < 3}
                      onError={(e) => {
                        console.error('Image failed to load:', screenshot);
                        e.currentTarget.style.display = 'none';
                      }}
                      onLoad={() => {
                        console.log('Image loaded successfully:', screenshot);
                      }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 text-lg">No screenshots available</div>
              </div>
            )}
          </div>
        );

      case 'preview':
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-semibold text-white text-center mb-8">Website Preview</h3>
            {hasPreview ? (
              <div className="max-w-6xl mx-auto">
                <div className="relative bg-zinc-800 rounded-lg overflow-hidden shadow-2xl">
                  <Image
                    src={project.websitePreviewImage!}
                    alt={`${project.title} Preview`}
                    width={1200}
                    height={800}
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 text-lg">Preview not available</div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-700 sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <button
              onClick={() => router.push('/#projects')}
              className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Back to Projects</span>
            </button>
            <h1 className="text-xl font-semibold text-white">{project.title}</h1>
            <div className="w-20"></div> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-zinc-800 border-b border-zinc-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-blue-400 border-b-2 border-blue-400 bg-zinc-700/50'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderTabContent()}
      </div>

      {/* Image Gallery Modal */}
      {project && hasScreenshots && (
        <ImageGallery
          images={project.screenshots!}
          alt={project.title}
          isOpen={galleryOpen}
          onClose={() => setGalleryOpen(false)}
          initialIndex={galleryIndex}
        />
      )}
    </div>
  );
}
